<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Tooltip from 'primevue/tooltip';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { getLegalBranchMeta } from '@/constants/predictusLegalBranch';
import { getLawsuitStatusMeta, LAWSUIT_STATUS_LABELS } from '@/constants/predictusLawsuitStatus';
import { isRecord, toNullableNumber, toNullableString } from '@/utils/coerce';
import { formatDate } from '@/utils/formatters';
import { getRiskLevelDisplay } from '@/utils/kyc';
import { LAWSUIT_DATE_OPTIONS, formatMoney, formatProcessNumber, positionMeta } from '@/utils/predictusLawsuitFormatters';
import type { KycCategory } from '@/types/kyc';

interface LawsuitRow {
  processNumber: string | null;
  mainSubject: string | null;
  caseClass: string | null;
  subjectSide: string | null;
  statusSlug: string | null;
  statusLabel: string | null;
  legalBranchSlug: string | null;
  courtName: string | null;
  state: string | null;
  filingDate: string | null;
  closeDate: string | null;
  claimValue: number | null;
  levelValue: number | null;
}

const props = defineProps<{ category: KycCategory }>();
defineOptions({ directives: { tooltip: Tooltip } });

const lawsuits = computed<LawsuitRow[]>(() => {
  const raw: unknown = props.category.data_json;
  if (!Array.isArray(raw)) return [];

  return raw.filter(isRecord).map((item) => {
    const statusSlug = toNullableString(item.status_slug);
    return {
      processNumber: toNullableString(item.process_number),
      mainSubject: toNullableString(item.main_subject),
      caseClass: toNullableString(item.case_class),
      subjectSide: toNullableString(item.subject_side),
      statusSlug,
      statusLabel: LAWSUIT_STATUS_LABELS[statusSlug ?? 'unknown'] ?? LAWSUIT_STATUS_LABELS.unknown,
      legalBranchSlug: toNullableString(item.legal_branch_slug),
      courtName: toNullableString(item.court_name),
      state: toNullableString(item.state),
      filingDate: toNullableString(item.filing_date),
      closeDate: toNullableString(item.close_date),
      claimValue: toNullableNumber(item.claim_value),
      levelValue: toNullableNumber(item.level_value),
    };
  });
});
</script>

<template>
  <div class="mt-3 w-full min-w-0 max-w-full [contain:inline-size]">
    <p class="mb-3 inline-flex items-center gap-1.5 rounded-md border border-surface-200 bg-surface-100 px-2.5 py-1 text-xs text-surface-600">
      <span class="text-sm font-semibold tabular-nums text-surface-900">{{ lawsuits.length }}</span>
      {{ lawsuits.length === 1 ? 'processo nesta análise' : 'processos nesta análise' }}
    </p>
    <DataTable :value="lawsuits" scrollable scroll-height="24rem" class="w-full max-w-full text-xs" sort-field="levelValue" :sort-order="-1">
      <template #empty>
        <div class="p-6 text-center text-xs text-surface-500">Nenhum processo encontrado nesta análise.</div>
      </template>

      <Column field="levelValue" header="Risco" sortable style="width: 4rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <span
            v-tooltip.top="getRiskLevelDisplay(row.levelValue).label"
            :aria-label="getRiskLevelDisplay(row.levelValue).label"
            tabindex="0"
            class="inline-flex h-7 w-7 items-center justify-center rounded-full border"
            :class="[getRiskLevelDisplay(row.levelValue).bgClass, getRiskLevelDisplay(row.levelValue).colorClass, getRiskLevelDisplay(row.levelValue).borderClass]"
          >
            <BaseIcon class="text-base">{{ getRiskLevelDisplay(row.levelValue).icon }}</BaseIcon>
          </span>
        </template>
      </Column>
      <Column field="processNumber" header="Processo" sortable style="min-width: 11rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <span class="font-medium text-surface-900">{{ formatProcessNumber(row.processNumber) }}</span>
        </template>
      </Column>
      <Column field="mainSubject" header="Assunto" style="min-width: 10rem; max-width: 14rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <span :title="row.mainSubject || ''" class="block text-surface-700">{{ row.mainSubject || '—' }}</span>
          <!-- <span v-if="row.caseClass" class="text-[10px] text-surface-500">{{ row.caseClass }}</span> -->
        </template>
      </Column>
      <Column field="subjectSide" header="Posição" style="min-width: 8rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <div v-if="row.subjectSide" class="flex items-center gap-1.5">
            <BaseIcon class="text-sm" :class="positionMeta(row.subjectSide).color">{{ positionMeta(row.subjectSide).icon }}</BaseIcon>
            <span class="text-surface-800">{{ row.subjectSide }}</span>
          </div>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="statusLabel" header="Status" sortable style="min-width: 8rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <Tag v-if="row.statusLabel" :value="row.statusLabel" :severity="getLawsuitStatusMeta(row.statusSlug, row.statusLabel).severity" class="!py-0 !px-1.5 !text-[10px]">
            <template v-if="getLawsuitStatusMeta(row.statusSlug, row.statusLabel).icon" #icon>
              <BaseIcon class="!text-[11px]">{{ getLawsuitStatusMeta(row.statusSlug, row.statusLabel).icon }}</BaseIcon>
            </template>
          </Tag>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="legalBranchSlug" header="Ramo" style="min-width: 8rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <span class="inline-flex items-center gap-1 text-surface-700">
            <BaseIcon class="text-sm text-surface-500">{{ getLegalBranchMeta(row.legalBranchSlug).icon }}</BaseIcon>
            {{ getLegalBranchMeta(row.legalBranchSlug).label }}
          </span>
        </template>
      </Column>
      <Column field="courtName" header="Tribunal" sortable style="min-width: 8rem; max-width: 14rem">
        <template #body="{ data: row }: { data: LawsuitRow }">
          <span :title="row.courtName || ''" class="block truncate text-surface-700">{{ row.courtName || '—' }}</span>
          <span v-if="row.state" class="text-[10px] text-surface-500">{{ row.state }}</span>
        </template>
      </Column>
      <Column field="filingDate" header="Distribuição" sortable style="min-width: 6.5rem">
        <template #body="{ data: row }: { data: LawsuitRow }">{{ formatDate(row.filingDate, LAWSUIT_DATE_OPTIONS, '—') }}</template>
      </Column>
      <Column field="claimValue" header="Valor da causa" sortable style="min-width: 8rem">
        <template #body="{ data: row }: { data: LawsuitRow }">{{ formatMoney(row.claimValue, null) }}</template>
      </Column>
      <Column field="closeDate" header="Encerramento" sortable style="min-width: 6.5rem">
        <template #body="{ data: row }: { data: LawsuitRow }">{{ formatDate(row.closeDate, LAWSUIT_DATE_OPTIONS, '—') }}</template>
      </Column>
    </DataTable>
  </div>
</template>
